import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt, FaUserShield, FaLock, FaExclamationTriangle } from 'react-icons/fa';

const AdvisorDataProtection = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1929] to-[#1E3A5F]">
      {/* Header */}
      <div className="bg-[#1A2942] border-b border-[#2D5A8F] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/register" className="flex items-center text-gray-300 hover:text-[#FFD700] transition-colors">
            <FaArrowLeft className="mr-2" />
            <span>Back to Registration</span>
          </Link>
          <div className="flex items-center space-x-2">
            <FaShieldAlt className="text-[#FFD700]" />
            <span className="text-white font-bold">Arth Gurukul</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] p-8 shadow-2xl">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b border-[#2D5A8F]">
            <div className="flex items-center justify-center mb-4">
              <FaUserShield className="text-[#FFD700] text-4xl" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Advisor Data Protection & Confidentiality Undertaking</h1>
            <h2 className="text-xl text-[#FFD700] font-semibold">Arth Gurukul</h2>
            <p className="text-gray-400">(An Initiative of MDRT School)</p>
            <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-400">
              <span>Version 1.0</span>
              <span className="text-[#FFD700]">|</span>
              <span>Effective Date: 27th Feb 2026</span>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert max-w-none space-y-8 text-gray-300">
            
            {/* Section 1 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">1. Purpose</h3>
              <p>This undertaking governs your access to and use of client data available through the Arth Gurukul platform.</p>
              <p className="font-semibold text-white">By registering as an Advisor, you agree to comply with all data protection obligations under applicable Indian laws, including the Digital Personal Data Protection Act, 2023.</p>
            </section>

            {/* Section 2 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">2. Permitted Use of Client Data</h3>
              <p>You may access client ("User") data strictly for:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Goal-based financial planning</li>
                <li>Risk profiling</li>
                <li>Insurance and financial gap analysis</li>
                <li>Client review discussions</li>
                <li>Lawful advisory services within your regulatory permissions</li>
              </ul>
              <p className="mt-4 font-semibold text-white">You shall access only those client accounts assigned to you.</p>
            </section>

            {/* Section 3 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">3. Confidentiality Obligations</h3>
              <p>You agree that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>All client information is confidential.</li>
                <li>You will not sell, share, transfer, or disclose client data to any unauthorized third party.</li>
                <li>You will not use client data for unrelated marketing without explicit client consent.</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3 flex items-center">
                <FaLock className="mr-2" /> 4. Data Security Responsibilities
              </h3>
              <p>You agree to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Maintain secure passwords and not share login credentials</li>
                <li>Use secure devices and networks</li>
                <li>Not download or store client data on unsecured systems</li>
                <li>Avoid creating unauthorized parallel databases</li>
                <li>Immediately report any suspected data breach</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3 flex items-center">
                <FaExclamationTriangle className="mr-2 text-red-400" /> 5. Prohibited Activities
              </h3>
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
                <p className="text-red-300 font-medium mb-2">You shall not:</p>
                <ul className="list-disc pl-6 space-y-1 text-red-200">
                  <li>Copy or extract bulk client data</li>
                  <li>Attempt to reverse engineer platform systems</li>
                  <li>Share platform access with unauthorized persons</li>
                  <li>Use the platform for unlawful activities</li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">6. Data Breach Notification</h3>
              <p>In the event of:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Unauthorized access</li>
                <li>Device theft</li>
                <li>Data leakage</li>
                <li>Cyber incident</li>
              </ul>
              <div className="mt-4 bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
                <p className="text-yellow-400 font-bold">You must notify Arth Gurukul within 24 hours.</p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">7. Regulatory Compliance</h3>
              <p>You confirm that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>You hold valid registrations/licenses required for your advisory activities (if applicable).</li>
                <li>The Platform is a financial literacy and analytical tool and does not execute transactions.</li>
                <li>You are solely responsible for advice provided to clients.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">8. Termination & Access Revocation</h3>
              <p>Arth Gurukul reserves the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Suspend or terminate your access in case of misuse</li>
                <li>Revoke access upon violation of this undertaking</li>
              </ul>
              <p className="mt-4 font-semibold text-white">Confidentiality obligations survive termination.</p>
            </section>

            {/* Section 9 */}
            <section>
              <h3 className="text-xl font-bold text-[#FFD700] mb-3">9. Legal Validity</h3>
              <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                <p className="text-white">By clicking "I Agree", you acknowledge that:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>You have read and understood this undertaking.</li>
                  <li className="font-semibold text-[#FFD700]">Your electronic acceptance constitutes a legally binding agreement equivalent to a physical signature.</li>
                </ul>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-[#2D5A8F] text-center">
            <Link 
              to="/register" 
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
            >
              <FaArrowLeft className="mr-2" />
              Back to Registration
            </Link>
          </div>
        </div>

        {/* Page Footer */}
        <div className="mt-8 text-center text-gray-400 text-xs">
          <p>© 2026 Arth Gurukul. All rights reserved.</p>
          <p className="mt-1">An Initiative of MDRT School</p>
        </div>
      </div>
    </div>
  );
};

export default AdvisorDataProtection;
